import { doc, getDoc } from "firebase/firestore";
import { firebaseHelper } from "ui";
import { parentProjectId, type AccessKind } from "@mattoboard/schemas";
import { accessUserFromPermissions, resourcePermFromDoc } from "ui/utils/usePermissions";
import { sharingV2Doc } from "ui/utils/workspace/sharingV2";
import { ensureSharingV2Flag } from "ui/utils/useDesignFirm";
import { setItemProject } from "ui/utils/workspace/accessApi";
import type { Permissions } from "ts-interfaces";
import { loadedProject } from "../../global-state/resolveProjectOwner";
import { useProjectSchemaDetails } from "../../global-state/useProjectSchemas";
import { associateBoardWithProject, associateMultipleBoardsWithProject } from "./boardsSchemeApi";
import {
  associateCollectionWithProject,
  associateMultipleCollectionsWithProject,
} from "./collectionsSchemeApi";
import { fileActionOf, projectShared, teamAnalytics, teamItem } from "../../team/teamAnalytics";

export * from "ui/utils/workspace/accessApi";

type Doc = Record<string, any>;

// A project in the workspace list, or the one an open project page loaded.
const knownProject = (id: string | null | undefined): Doc | undefined => {
  if (!id) return undefined;
  const page = useProjectSchemaDetails.getState().projectsData?.[id]?.project;
  return loadedProject(id) ?? (page?.id === id ? page : undefined);
};

// The project a filing names, with its team's flag loaded: the known copy,
// else a read (a project from another workspace, or a cold store).
export async function filingProject(id: string | null | undefined) {
  if (!id) return undefined;
  let project = knownProject(id);
  if (!project) {
    try {
      const snap = await getDoc(doc(firebaseHelper.db, "projectsScheme", id));
      project = snap.exists() ? { id, ...snap.data() } : undefined;
    } catch {
      // Unreadable, missing, or a malformed id (a hand-edited ?project=): it decides nothing.
    }
  }
  await ensureSharingV2Flag(firebaseHelper.db, project?.design_firm_id);
  return project;
}

// Sharing v2 governs a filing when the item's team, or the project it leaves
// or enters, has the flag on; the backend then owns the pointer and arrays.
export async function filesThroughBackend(
  kind: AccessKind,
  item: Doc | null | undefined,
  projectId: string | null,
) {
  if (!item) return false;
  const parentId = parentProjectId(kind, item);
  const target = await filingProject(projectId);
  const parent = parentId === projectId ? target : await filingProject(parentId);
  await ensureSharingV2Flag(firebaseHelper.db, item.design_firm_id);
  return sharingV2Doc(item) || sharingV2Doc(target) || sharingV2Doc(parent);
}

// canFile on the client: the item's owner, a project in the same team, and
// edit access to that project. A project with no access list yet takes the
// backend's legacy rule (authorizeFiling): its owner, a member of its team, or
// a projectWrite grant.
export const canFileInto = (
  kind: AccessKind,
  item: Record<string, any>,
  project: Record<string, any>,
  uid: string,
  permissions: Permissions | null,
) => {
  const owner = kind === "board" ? item.user : kind === "concept" ? item.owner_id : item.uid;
  if (owner !== uid || !item.design_firm_id || project.design_firm_id !== item.design_firm_id) {
    return false;
  }
  if (!project.access) {
    return (
      project.owner === uid ||
      accessUserFromPermissions(uid, permissions).teams.includes(project.design_firm_id) ||
      (permissions?.projectWrite ?? []).includes(project.id)
    );
  }
  return resourcePermFromDoc(permissions, uid, "project", project) === "read_write";
};

const trackFiling = (
  kind: AccessKind,
  item: Doc | undefined,
  projectId: string | null,
  uid: string,
  bulkCount?: number,
) => {
  const action = item && fileActionOf(parentProjectId(kind, item), projectId);
  if (!item || !action) return;
  teamAnalytics.itemFiled(teamItem(kind, item.id, item, "editor", uid), {
    action,
    projectId,
    projectShared: projectShared(knownProject(projectId)),
    bulkCount,
  });
};

// Files (or unfiles) a board. Returns the stored board either way.
export async function fileBoard(
  board: Record<string, any>,
  projectId: string | null,
  uid: string,
  permissions: Permissions | null,
) {
  if (!(await filesThroughBackend("board", board, projectId))) {
    const stored = await associateBoardWithProject(board.id, projectId, uid, permissions);
    trackFiling("board", board, projectId, uid);
    return stored;
  }
  await setItemProject("board", board.id, projectId, board.design_firm_id);
  trackFiling("board", board, projectId, uid);
  const snap = await getDoc(doc(firebaseHelper.db, "projects", board.id));
  return { id: board.id, ...snap.data() } as any;
}

// Files (or unfiles) a collection. Under sharing v2, returns the new arrays
// for the caller's local copy; {} on the legacy path.
export async function fileCollection(
  collection: Record<string, any>,
  projectId: string | null,
  uid: string,
  permissions: Permissions | null,
): Promise<{ readers?: string[]; editors?: string[] }> {
  if (!(await filesThroughBackend("collection", collection, projectId))) {
    await associateCollectionWithProject(collection.id, projectId, uid, permissions);
    trackFiling("collection", collection, projectId, uid);
    return {};
  }
  const { readers, editors } = await setItemProject(
    "collection",
    collection.id,
    projectId,
    collection.design_firm_id,
  );
  trackFiling("collection", collection, projectId, uid);
  return { readers, editors };
}

// Bulk filing from a project page: an item goes through the backend when the
// project or the item is flagged, the rest through the legacy batch (all of
// them, unchanged, when nothing is). Refused ids are left out of added/removed,
// `arrays` holds each filed item's new arrays, `error` the first refusal.
export async function fileManyWithProject(
  kind: "board" | "collection",
  addIds: string[],
  removeIds: string[],
  projectId: string,
  uid: string,
  permissions: Permissions | null,
  items: Doc[],
) {
  const projectV2 = sharingV2Doc(await filingProject(projectId));
  const byId = new Map(items.map((i) => [i.id, i]));
  // Each item's own team flag decides too, so load it first (as filesThroughBackend does).
  const teams = new Set([...addIds, ...removeIds].map((id) => byId.get(id)?.design_firm_id));
  await Promise.all([...teams].map((firm) => ensureSharingV2Flag(firebaseHelper.db, firm)));
  const viaBackend = (id: string) => projectV2 || sharingV2Doc(byId.get(id));
  const backendAdd = addIds.filter(viaBackend);
  const backendRemove = removeIds.filter(viaBackend);
  const legacyAdd = addIds.filter((id) => !viaBackend(id));
  const legacyRemove = removeIds.filter((id) => !viaBackend(id));
  if (!projectV2 && (legacyAdd.length || legacyRemove.length || !backendAdd.length && !backendRemove.length)) {
    const legacyBatch =
      kind === "board" ? associateMultipleBoardsWithProject : associateMultipleCollectionsWithProject;
    await legacyBatch(legacyAdd, legacyRemove, projectId, uid, permissions);
  }
  const r = await fileEachThroughBackend(kind, backendAdd, backendRemove, projectId, (id) =>
    byId.get(id)?.design_firm_id,
  );
  const added = [...legacyAdd, ...r.added];
  const removed = [...legacyRemove, ...r.removed];
  const bulk = added.length + removed.length;
  added.forEach((id) => trackFiling(kind, byId.get(id), projectId, uid, bulk));
  removed.forEach((id) => trackFiling(kind, byId.get(id), null, uid, bulk));
  return { ...r, added, removed };
}

// One backend call per item, all in flight together.
export async function fileEachThroughBackend(
  kind: "board" | "collection",
  addIds: string[],
  removeIds: string[],
  projectId: string,
  teamOf: (id: string) => string | null | undefined = () => null,
) {
  const jobs = [
    ...addIds.map((id) => ({ id, target: projectId as string | null })),
    ...removeIds.map((id) => ({ id, target: null })),
  ];
  const results = await Promise.allSettled(
    jobs.map(({ id, target }) => setItemProject(kind, id, target, teamOf(id))),
  );
  const added: string[] = [];
  const removed: string[] = [];
  const arrays: Record<string, { readers: string[]; editors: string[] }> = {};
  let error: Error | null = null;
  results.forEach((r, i) => {
    const { id, target } = jobs[i];
    if (r.status === "rejected") {
      error ??= r.reason as Error;
      return;
    }
    arrays[id] = { readers: r.value.readers, editors: r.value.editors };
    (target ? added : removed).push(id);
  });
  return { added, removed, arrays, error };
}

// Bulk pickers: in a flagged project, an item is pickable when the user may
// file it there (canFile) or, if it is already in, take it out (canMoveOut).
export const pickableForProject = (
  kind: "board" | "collection",
  item: Record<string, any>,
  projectId: string | null | undefined,
  uid: string | undefined,
  permissions: Permissions | null,
) => {
  const project = knownProject(projectId);
  if (!projectId || !uid || !sharingV2Doc(project)) return true;
  const owner = kind === "board" ? item.user : item.uid;
  if (parentProjectId(kind, item) === projectId) return owner === uid || project!.owner === uid;
  return canFileInto(kind, item, project!, uid, permissions);
};

